<template>
  <div class="d-flex align-items-start resource-dropdown">
    <ValidationProvider :vid="vid" :name="$attrs.name" :rules="rules" style="flex: 1">
      <b-form-group slot-scope="{ valid, errors }" v-bind="$attrs">
        <template #label>
          <label>
            {{ $attrs.label }}
            <span v-if="$attrs.required || rules.includes('required')" class="text-danger font-bold"
              >*</span
            >
          </label>
        </template>
        <v-select
          v-model="selectedHSCode"
          :options="options"
          :filterable="false"
          placeholder="Select HSCode"
          :loading="loadingSelectedHSCode"
          :clearable="true"
          :class="!!errors[0] ? 'is-invalid' : ''"
          @search="onSearch"
          :disabled="$attrs.disabled"
          :state="errors[0] ? false : rules && rules.includes('required') && valid ? true : null"
          v-bind="$attrs"
        >
          <template #no-options>
            <div class="text-center text-muted">No hsCodes found</div>
          </template>

          <template #spinner="{ loading }">
            <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </template>

          <template #selected-option="{ id, code, name }">
            <strong>{{ code }}</strong>
          </template>

          <template #option="{ code, name }">
            <b-row>
              <div class="d-flex flex-column pr-1 mr-1 border-right">
                <small class="text-muted d-block">CODE</small>
                <strong>{{ code }}</strong>
              </div>
              <div class="d-flex flex-column">
                <small class="text-muted d-block">NAME</small>
                <strong class="mr-1">{{ name }}</strong>
              </div>
            </b-row>
          </template>
        </v-select>

        <b-form-invalid-feedback id="inputLiveFeedback">
          {{ errors[0] ? errors[0].replace('{field}', $attrs.label) : '' }}
        </b-form-invalid-feedback>
      </b-form-group>
    </ValidationProvider>
    <create-hs-code-modal @submit="onHSCodeCreated" />
  </div>
</template>

<script>
import { BFormGroup, BFormInvalidFeedback } from 'bootstrap-vue';
import { api } from '@/utils/api';
import { BCol, BRow } from 'bootstrap-vue';
import { ValidationProvider } from 'vee-validate';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, watch, onMounted, onUnmounted } from '@vue/composition-api';
import store from '@/store';
import HSCodeStoreModule from '@/views/apps/fbr-invoice/hs-code/HSCodeStoreModule.js';
import CreateHSCodeModal from '@/views/apps/fbr-invoice/hs-code/CreateHSCodeModal.vue';

export default {
  name: 'HSCodeDropdownField',
  components: {
    ValidationProvider,
    vSelect,
    BRow,
    BCol,
    BFormGroup,
    BFormInvalidFeedback,

    // Custom Components
    'create-hs-code-modal': CreateHSCodeModal,
  },
  props: {
    vid: {
      type: String,
    },
    name: {
      type: String,
      default: 'HSCode',
    },
    value: {
      type: [Object, Number, String],
      default: () => null,
    },
    rules: {
      type: [Object, String],
      default: 'required',
    },
  },
  setup(props, { emit }) {
    const HS_CODE_STORE_MODULE_NAME = 'hs-code';

    // Register module
    if (!store.hasModule(HS_CODE_STORE_MODULE_NAME))
      store.registerModule(HS_CODE_STORE_MODULE_NAME, HSCodeStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(HS_CODE_STORE_MODULE_NAME))
        store.unregisterModule(HS_CODE_STORE_MODULE_NAME);
    });

    // Reactive references
    const options = ref([]);
    const searchTerm = ref('');
    const pageNumber = ref(1);
    const loadingHSCodes = ref(false);
    const loadingSelectedHSCode = ref(false);
    const selectedHSCode = ref(null);
    const searchTimeout = ref(null);

    // Computed properties
    const businessID = computed(() => store.state.user?.businessID);

    // Methods
    const fetchHSCodes = async () => {
      loadingHSCodes.value = true;
      try {
        const response = await api.get(`/HSCode/GetByPagination/${businessID.value}`, {
          params: {
            searchTerm: searchTerm.value,
            pageNumber: pageNumber.value,
            pageSize: 20,
          },
        });

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.data &&
          response.data.data.items
        ) {
          options.value = [...response.data.data.items];
        } else {
          console.error(
            '=== HSCodeDropdownField options Error in paginated hsCode data format:',
            response.data
          );
          options.value = [];
        }
      } catch (error) {
        console.error('=== HSCodeDropdownField options Error fetching hsCodes:', error);
        options.value = [];
      } finally {
        loadingHSCodes.value = false;
      }
    };

    const onHSCodeCreated = (hsCodeResponse) => {
      console.log(
        '=== HSCodeDropdownField onHSCodeCreated:',
        JSON.stringify(hsCodeResponse, null, 2)
      );
      selectedHSCode.value = hsCodeResponse;
    };

    const onSearch = (searchTermValue) => {
      // Debounce search
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTerm.value = searchTermValue;

      if (!searchTermValue || searchTermValue.trim() === '') {
        searchTerm.value = '';
        fetchHSCodes();
        return;
      }

      searchTimeout.value = setTimeout(() => {
        console.log('=== HSCodeDropdownField fetchHSCodes', searchTerm.value);
        fetchHSCodes();
      }, 350);
    };

    // Watchers
    watch(
      selectedHSCode,
      (newVal) => {
        emit('input', newVal?.id);
      },
      { immediate: true }
    );

    // Lifecycle hooks
    onMounted(() => {
      const hsCodeId = props.value;

      // Fetch initial data
      fetchHSCodes();

      if (hsCodeId) {
        loadingSelectedHSCode.value = true;
        // You can add logic here to fetch the specific HS Code if needed
        store
          .dispatch('hs-code/fetchHSCode', { id: hsCodeId })
          .then((hsCode) => {
            selectedHSCode.value = hsCode;
          })
          .catch((error) => {
            console.log('error:', error);
          })
          .finally(() => {
            loadingSelectedHSCode.value = false;
          });
      }
    });

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    });

    return {
      // Reactive references
      options,
      searchTerm,
      pageNumber,
      loadingHSCodes,
      loadingSelectedHSCode,
      selectedHSCode,
      searchTimeout,

      // Computed properties
      businessID,

      // Methods
      fetchHSCodes,
      onSearch,
      onHSCodeCreated,
    };
  },
};
</script>

<style lang="scss" scoped>
.v-select {
  &.is-invalid {
    .vs__dropdown-toggle {
      border-color: #dc3545;
    }
  }

  .vs__selected {
    width: 100%;
  }

  ::v-deep {
    .vs__dropdown-menu {
      max-height: 350px;
    }
  }
}
</style>
